import React, { useState, useEffect } from 'react';
import { Star, MessageSquare, Trash2, Send, TrendingUp, User, Calendar, Filter } from 'lucide-react';
import StarRating from '../reviews/StarRating';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, responded
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reviewsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/reviews/admin/all`),
        axios.get(`${API_URL}/api/reviews/admin/stats`)
      ]);

      setReviews(reviewsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Erro ao carregar avaliações (admin):', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (reviewId) => {
    if (!responseText.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/reviews/admin/${reviewId}/respond`,
        { response: responseText }
      );

      // Update local state
      setReviews(reviews.map(r => 
        r.id === reviewId 
          ? { ...r, admin_response: responseText, admin_response_at: new Date().toISOString() }
          : r
      ));
      
      setRespondingTo(null);
      setResponseText('');
      fetchData(); // Refresh stats
    } catch (err) {
      alert('Erro ao enviar resposta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta avaliação?')) return;

    try {
      await axios.delete(`${API_URL}/api/reviews/admin/${reviewId}`);

      setReviews(reviews.filter(r => r.id !== reviewId));
      fetchData(); // Refresh stats
    } catch (err) {
      alert('Erro ao excluir avaliação');
    }
  };

  const filteredReviews = reviews.filter(r => {
    if (filter === 'pending') return !r.admin_response;
    if (filter === 'responded') return !!r.admin_response;
    return true;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-reviews">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_reviews}</p>
                <p className="text-sm text-gray-400">Total de Avaliações</p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.average_rating}</p>
                <p className="text-sm text-gray-400">Média Geral</p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.pending_responses}</p>
                <p className="text-sm text-gray-400">Aguardando Resposta</p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
            <p className="text-sm text-gray-400 mb-2">Top Cursos</p>
            {stats.top_courses.slice(0, 2).map((course) => (
              <div key={course.course_title} className="flex items-center justify-between text-sm">
                <span className="text-white truncate">{course.course_title}</span>
                <span className="text-yellow-400 flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" />
                  {course.average_rating}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 mb-4">
        <Filter className="w-4 h-4 text-gray-400" />
        <div className="flex gap-2">
          {[
            { key: 'all', label: 'Todas' },
            { key: 'pending', label: 'Pendentes' },
            { key: 'responded', label: 'Respondidas' }
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                filter === key
                  ? 'bg-red-600 text-white'
                  : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Reviews List */}
      {filteredReviews.length === 0 ? (
        <div className="text-center py-12 bg-zinc-800/50 rounded-lg">
          <Star className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">Nenhuma avaliação encontrada</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReviews.map((review) => (
            <div 
              key={review.id} 
              className="bg-zinc-800 rounded-lg p-5 border border-zinc-700"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-white font-medium">
                        {review.user_name}
                        {review.is_anonymous && (
                          <span className="text-xs text-gray-500 ml-2">(anônimo público)</span>
                        )}
                      </span>
                    </div>
                    <StarRating rating={review.rating} size="sm" />
                  </div>
                  <p className="text-sm text-gray-400">{review.course_title}</p>
                  <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(review.created_at)}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {!review.admin_response && (
                    <button
                      onClick={() => {
                        setRespondingTo(review.id);
                        setResponseText('');
                      }}
                      className="p-2 bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/30 transition-colors"
                      title="Responder"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(review.id)}
                    className="p-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-colors"
                    title="Excluir"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Comment */}
              {review.comment && (
                <p className="text-gray-300 text-sm bg-zinc-900 p-3 rounded-lg mb-3">
                  "{review.comment}"
                </p>
              )}

              {/* Admin Response */}
              {review.admin_response && (
                <div className="pl-4 border-l-2 border-red-600 mt-3">
                  <p className="text-xs text-red-500 font-semibold mb-1">Sua resposta:</p>
                  <p className="text-gray-400 text-sm">{review.admin_response}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDate(review.admin_response_at)}
                  </p>
                </div>
              )}

              {/* Response Form */}
              {respondingTo === review.id && (
                <div className="mt-4 p-4 bg-zinc-900 rounded-lg">
                  <label className="text-sm text-gray-400 mb-2 block">
                    Responder avaliação:
                  </label>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    maxLength={500}
                    rows={3}
                    placeholder="Escreva sua resposta..."
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-red-500 resize-none mb-3"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {responseText.length}/500 caracteres
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setRespondingTo(null)}
                        className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => handleRespond(review.id)}
                        disabled={!responseText.trim() || submitting}
                        className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        {submitting ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        Enviar
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
